import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { LEGAL_COPY } from "../lib/legalCopy";

const CONSENT_KEY = "tvcabsat:cookie-consent";

const readConsent = () => {
  try {
    return Boolean(window.localStorage.getItem(CONSENT_KEY));
  } catch {
    // Storage unavailable (private mode / disabled) — treat as no consent so
    // the banner shows; nothing will be persisted on accept either.
    return false;
  }
};

/**
 * Non-blocking cookie banner — appears at the bottom of the viewport on
 * first visit, dismissible by clicking "Aceitar" or the X. Once accepted,
 * the choice is persisted in localStorage and the banner doesn't show again.
 */
export const CookieBanner = () => {
  const { lang } = useLang();
  const t = (LEGAL_COPY[lang] || LEGAL_COPY.pt).cookieBanner;
  // Lazy initializer reads the consent flag synchronously on first render so
  // we never need a useEffect just to toggle visibility — avoids an extra
  // render and the `react-hooks/set-state-in-effect` lint warning.
  const [visible, setVisible] = useState(() => !readConsent());

  const accept = () => {
    try {
      window.localStorage.setItem(CONSENT_KEY, "1");
    } catch {
      // Persistence failed (private mode / quota) — banner will reappear
      // next visit; safe to ignore.
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-[1100px] rounded-2xl border border-slate-300 bg-white/95 p-4 shadow-2xl shadow-slate-900/20 backdrop-blur-xl md:bottom-6 md:inset-x-6 md:p-5"
    >
      <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
        <div className="flex-1 pr-2">
          <h3 className="font-heading text-sm font-bold text-slate-900 md:text-base">
            {t.title}
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-600 md:text-sm">
            {t.body}
          </p>
        </div>
        <div className="flex w-full flex-shrink-0 items-center gap-2 md:w-auto">
          <Link
            to="/politica-privacidade"
            data-testid="cookie-learn-more-link"
            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:text-slate-900 md:text-sm"
          >
            {t.learnMore}
          </Link>
          <button
            type="button"
            onClick={accept}
            data-testid="cookie-accept-btn"
            className="flex-1 rounded-full bg-blue-600 px-5 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-500 md:flex-none md:text-sm"
          >
            {t.accept}
          </button>
          <button
            type="button"
            onClick={accept}
            aria-label="Close"
            data-testid="cookie-close-btn"
            className="hidden h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 md:inline-flex"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};
